import xss from 'xss';
import { safeAttrValue, whiteList } from 'xss';
import { FILE_URL_ID_PATTERN } from 'shared/media';

const validAttrs = [
  'href',
  'download',
  'data-state',
  'data-bg',
  'data-fg',
  'data-align',
  'src',
  'data-content',
  'data-size',
  'controls',
  'alt',
  'preload',
  'poster',
  'style',
  'width',
  'height',
  'title',
  'frameborder',
  'allow',
  'allowfullscreen',
  'contenteditable',
  'class',
];

const tags = Object.keys(whiteList).concat(
  'mark',
  'text-color',
  'raw-html',
  'embed-html',
  'style',
  'figure',
  'figcaption',
  'template-field',
  'hr',
  'u',
  's',
);

const toWhitelist = (xs: string[]) =>
  xs.reduce(
    (acc, tag) => {
      acc[tag] = validAttrs;
      return acc;
    },
    {} as Record<string, string[]>,
  );

const customWhiteList = toWhitelist(tags);
const customWhiteListWithIframe = toWhitelist([...tags, 'iframe']);

/**
 * A list of tags that are acceptable in emails that might be sent by spammers.
 */
export const spammerWhiteList = toWhitelist([
  'mark',
  'figure',
  'figcaption',
  'template-field',
  'text-color',
  'p',
  'em',
  'strong',
  'b',
  'u',
  's',
  'i',
  'ol',
  'ul',
  'li',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'br',
  'hr',
  'img',
]);

export function sanitizeHtml({
  html,
  allowAllStyleAttributes = false,
  allowIframe = false,
}: {
  html: string;
  allowAllStyleAttributes?: boolean;
  allowIframe?: boolean;
}) {
  return xss(html, {
    whiteList: allowIframe ? customWhiteListWithIframe : customWhiteList,
    css: !allowAllStyleAttributes,
  });
}

/**
 * Sanitize invitations HTML content with a tighter whitelist, for likely spammer abuseable content.
 */
export function sanitizeInvitationHTML({
  html,
  allowAllStyleAttributes = false,
}: {
  html: string;
  allowAllStyleAttributes?: boolean;
}) {
  return xss(html, {
    whiteList: spammerWhiteList,
    css: !allowAllStyleAttributes,
  });
}

/**
 * Tags that survive `sanitizeSubmissionHtml`. Deliberately narrower than the
 * editor's own whitelist: assignment answers are written by students and land
 * in a guide's inbox, where scripts, styles, iframes and custom elements either
 * get stripped by the mail client anyway or are actively dangerous.
 *
 * Two constraints on widening this list:
 *
 * - `inlineMailStyles` (the second sanitizer pass every email body goes through)
 *   wires both `onTagAttr` and `onIgnoreTagAttr` to a handler that echoes
 *   attributes back verbatim, bypassing js-xss's `safeAttrValue`. This list is
 *   therefore the *only* defense on attribute values for student content.
 * - A tag that isn't in js-xss's default whitelist will be escaped by that same
 *   second pass and reach the guide as literal `&lt;tag&gt;` text.
 *
 * `div` is deliberately absent: a stray `</div>` in a student's answer would
 * otherwise close the email's card and push the review link and footer outside it.
 */
const submissionWhiteList: Record<string, string[]> = {
  p: [],
  br: [],
  hr: [],
  span: [],
  strong: [],
  b: [],
  em: [],
  i: [],
  u: [],
  s: [],
  mark: [],
  ol: ['start'],
  ul: [],
  li: [],
  blockquote: [],
  h1: [],
  h2: [],
  h3: [],
  h4: [],
  h5: [],
  h6: [],
  pre: [],
  code: [],
  figure: [],
  figcaption: [],
  a: ['href', 'title'],
  img: ['src', 'alt', 'width', 'height'],
  table: [],
  thead: [],
  tbody: [],
  tr: [],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan'],
};

/**
 * Tags whose bodies must go with them — no mail client plays video or audio, or
 * runs embedded HTML.
 *
 * Never put a VOID tag (`img`, `br`, `hr`) in `stripIgnoreTagBody`. js-xss
 * tracks the start of a to-be-removed range in a single cursor that only a
 * *closing* tag resets, so a void tag opens the range and never closes it — and
 * the next closing media tag then deletes everything in between, including the
 * student's own prose. Void tags are removed cleanly by `stripIgnoreTag` alone.
 */
const unrenderableBodyTags = ['video', 'audio', 'iframe', 'embed-html', 'raw-html', 'object'];

/**
 * Callers must tell the reader something was left behind rather than dropping it
 * silently — see `hasUnrenderableMedia`. Built once; these run per answer, per
 * email.
 */
const unrenderablePatterns = unrenderableBodyTags.map((tag) => new RegExp(`<${tag}[\\s/>]`, 'i'));

/**
 * js-xss implements `stripIgnoreTagBody` by emitting these sentinels and
 * splicing out the range between them. A genuinely self-closing body tag
 * (`<audio/>`) has no closing partner, so its opening sentinel survives and
 * would reach the reader as the literal text `[removed]`.
 *
 * Collateral: a student who writes "[removed]" in their answer loses those
 * characters. The sentinels are indistinguishable from the literal string.
 */
const strippedTagSentinel = /\[\/?removed\]/g;

/**
 * Sanitizes a student's assignment answer for display inside an email.
 * Keeps basic formatting, links and tables; drops everything else.
 *
 * `fileOrigin` confines `img src` to this platform's own uploads. Without it,
 * js-xss's default `safeAttrValue` would allow any `http(s)` or `data:image/`
 * src, which in a guide's inbox means a student could plant a tracking pixel
 * that reports when each coach opened their submission, or paste a base64 image
 * far past the size at which Gmail clips the message.
 */
export function sanitizeSubmissionHtml(html: string, opts: { fileOrigin?: string } = {}) {
  return xss(html, {
    whiteList: submissionWhiteList,
    stripIgnoreTag: true,
    stripIgnoreTagBody: ['script', 'style', ...unrenderableBodyTags],
    safeAttrValue(tag, name, value, cssFilter) {
      // Run js-xss's own rules FIRST: its return value is what gets written
      // between the quotes, and escaping the value is the last thing it does.
      // Allow-checking the raw input instead would let a literal `"` close the
      // attribute and inject another one.
      const safe = safeAttrValue(tag, name, value, cssFilter);
      if (tag === 'img' && name === 'src') {
        return isOwnFileUrl(safe, opts.fileOrigin) ? safe : '';
      }
      return safe;
    },
  }).replace(strippedTagSentinel, '');
}

/**
 * True for `/files/<uuid>`, relative or absolute on our own origin.
 *
 * The `startsWith('/files/')` test after the slice is load-bearing: it is what
 * rejects `https://our-origin.evil.com/files/<uuid>.png`, whose remainder is
 * `.evil.com/files/…`. Don't simplify this into the regex alone.
 */
function isOwnFileUrl(value: string, fileOrigin?: string) {
  const origin = trimTrailingSlash(fileOrigin);
  const path = origin && value.startsWith(origin) ? value.slice(origin.length) : value;
  return path.startsWith('/files/') && FILE_URL_ID_PATTERN.test(path);
}

function trimTrailingSlash(url?: string) {
  return (url || '').replace(/\/$/, '');
}

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * True if the content holds media that can't survive the trip into an inbox, so
 * the email can point the reader at the full submission instead of quietly
 * showing them less than the student wrote. Files are handled separately by
 * `resolveSubmissionFiles`, which knows which ones are readable without a login.
 */
export function hasUnrenderableMedia(html: string) {
  return unrenderablePatterns.some((pattern) => pattern.test(html));
}

const UUID_SOURCE = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/**
 * A `src`/`href` pointing at one of OUR uploads. Newer uploads are stored
 * absolute and older ones relative, so both shapes have to match — an absolute
 * private URL that slipped through would otherwise reach the inbox as a broken
 * image with no notice at all.
 *
 * The origin is anchored rather than left open: a student linking to some other
 * site whose URL happens to carry `/files/<uuid>` should keep their link, not
 * have it silently retargeted at us and reported as unshowable.
 */
function fileUrlPattern(origin: string) {
  const prefix = origin ? `(?:${escapeRegExp(origin)})?` : '';
  return new RegExp(`(src|href)\\s*=\\s*"${prefix}/files/(${UUID_SOURCE})([^"]*)"`, 'gi');
}

/**
 * Rewrites the uploads in a submission for an inbox.
 *
 * A stored upload is a *relative* URL that the app serves from `/files/<id>`,
 * redirecting to the CDN only when the file is public. A mail client fetches
 * anonymously through its own proxy, so a relative URL resolves against the
 * wrong host and a private one 401s either way. Public files are therefore
 * absolutized so they load; private ones are removed — images dropped entirely,
 * links reduced to their label — and counted so the reader can be told.
 */
export function resolveSubmissionFiles(
  html: string,
  opts: { baseUrl: string; publicFileIds: Set<string> },
) {
  const origin = trimTrailingSlash(opts.baseUrl);
  let hiddenCount = 0;
  const resolved = html.replace(fileUrlPattern(origin), (_match, attr, rawId, rest) => {
    // Postgres hands back lowercase ids; a url could carry any case.
    const id = rawId.toLowerCase();
    if (opts.publicFileIds.has(id)) {
      return `${attr}="${origin}/files/${id}${rest}"`;
    }
    ++hiddenCount;
    // Dropping `href` leaves the label as plain text; the `img` itself is
    // removed below, since an image with no src is just a broken icon.
    return attr.toLowerCase() === 'href' ? '' : 'data-hidden="1"';
  });

  // Also drop images the sanitizer emptied the src on — one pointed somewhere we
  // won't load from. Left in place they render as a broken icon telling the
  // reader nothing; counted here, the existing notice explains them.
  const withoutHidden = resolved.replace(/<img[^>]*data-hidden="1"[^>]*>/gi, '');
  const withoutSrcless = withoutHidden.replace(/<img(?![^>]*\ssrc="[^"])[^>]*>/gi, () => {
    ++hiddenCount;
    return '';
  });

  return { html: withoutSrcless, hiddenCount };
}

export function sanitizeBioHtml(html: string) {
  return xss(html, {
    stripIgnoreTag: true,
    whiteList: {
      p: [],
      // Both of bold and italic tags are supported
      strong: [],
      b: [],
      em: [],
      i: [],
      u: [],
      s: [],
      br: [],
      blockquote: [],
      a: ['href'],
    },
  });
}

export function sanitizeCommentHtml(html: string) {
  return xss(html, {
    stripIgnoreTag: true,
    whiteList: {
      p: [],
      // Both of bold and italic tags are supported
      strong: [],
      b: [],
      u: [],
      s: [],
      em: [],
      i: [],
      br: [],
      blockquote: [],
      a: ['href', 'download', 'data-state'],
      // Inline media cards (images/video/audio/files) inserted via the comment
      // editor serialize to an element carrying data-state (a <figure> for
      // images, otherwise the media element itself); the readonly renderer
      // rebuilds the card from data-state. Without these the graphic is
      // stripped here on save.
      figure: ['data-state', 'data-align', 'class'],
      figcaption: [],
      img: ['src', 'alt', 'width', 'height', 'class', 'data-state'],
      video: ['src', 'controls', 'preload', 'poster', 'width', 'height', 'data-state'],
      audio: ['src', 'controls', 'preload', 'data-state'],
    },
  });
}

/*
 * Sanitizes MeiliSearch results.
 * Allows `strong` tags only for highlighting.
 */
export function sanitizeSearchResults(html: string) {
  return xss(html, {
    stripIgnoreTag: true,
    whiteList: {
      em: [],
    },
  });
}

/*
 * Strips all the html tags
 * and keeps only the plain text.
 */
export function stripAllTags(html: string) {
  return xss(html, {
    whiteList: {},
    stripIgnoreTag: true,
    // the script tag is a special case, we need
    // to filter out its content
    stripIgnoreTagBody: ['script'],
  });
}

/*
 * Cleans the HTML content by stripping all the tags,
 * replacing special characters and appending `...`
 * after the character limit.
 */
export function cleanHTMLContent({
  content,
  characterLimit = 140,
}: {
  content: string;
  characterLimit?: number;
}) {
  // Strip all html tags
  let str = stripAllTags(content);
  // Strip all &nbsp; characters caused by extra spaces within the comment
  str = str.replace(/(&nbsp;|&amp;|&quot;|&#34;|&#39;)+/g, (x) => {
    switch (x) {
      case '&nbsp;':
        return ' ';
      case '&amp;':
      case '&#38;':
        return '&';
      case '&quot;':
      case '&#34;':
        return '"';
      case '&#39;':
        return `'`;
      default:
        return '';
    }
  });
  // Append `...` after the character limit
  str = str.length > characterLimit ? `${str.substring(0, characterLimit)}...` : str;
  return str;
}
